/**
 * @fileoverview Shared conversion of active dashboard global-filter values into
 *             `DexieQueryFilter[]` for a single widget.
 * @module local-db/reporting/dashboardGlobalFilters
 *
 * Responsibilities: - Convert `Record<filterId, value>` into `DexieQueryFilter[]`
 *                     honoring `filterConfig.targetWidgets` scoping, `FilterValue`
 *                     objects, scalar/array/range values, and operator mapping.
 * Dependencies:     - ./reportingTypes (DashboardConfig, DashboardWidget,
 *                     DexieQueryFilter, FilterOperator).
 * Architecture:     - Package-first extraction shared by tenant-web
 *                     (`buildGlobalQueryFilters`) and system-web
 *                     (`systemDashboardDataService`). Host-specific
 *                     member→collection-field resolution is injected via
 *                     `resolveField` — this module never imports host catalog code.
 * Notes:            - Numeric operands are preserved for relational operators so
 *                     `evaluateFilter` compares numerically; text filter widgets
 *                     default to `contains`.
 *                   - Unscoped filters apply only to widgets sharing the filter's
 *                     cube (configured cubeId, or the member's cube prefix when it
 *                     matches a cube in use on the dashboard); explicit
 *                     `targetWidgets` lists override this scoping.
 */

import type {
  DashboardConfig,
  DashboardWidget,
  DexieQueryFilter,
  FilterOperator,
} from './reportingTypes';

/** Structural view of a ui-admin `FilterValue` — kept local so local-db never
 *  depends on ui-admin. */
interface FilterValueLike {
  field?: string;
  operator?: string;
  value: unknown;
}

function isFilterValueLike(entry: unknown): entry is FilterValueLike {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    !Array.isArray(entry) &&
    'operator' in entry &&
    'value' in entry
  );
}

/**
 * @process buildGlobalDashboardQueryFilters
 * Steps:   1. Inspect filter widgets in `config.widgets` (`type === 'filter'`)
 *             and `config.globalFilters` for each active filter entry.
 *          2. Skip entries whose filter widget scopes `targetWidgets` away
 *             from `widget.id`, and entries with null/empty values.
 *          3. Resolve the member field via the injected host `resolveField`.
 *          4. Convert scalar, array, or `{ from, to }` range values into
 *             `DexieQueryFilter` rows (equals / notEquals / contains / gt /
 *             gte / lt / lte / inDateRange).
 * Inputs:  config, widget, globalFilters, resolveField.
 * Outputs: DexieQueryFilter[].
 * Failure Handling: Null/empty filter values are ignored.
 * Idempotency: REQUIRED — pure function.
 */
export function buildGlobalDashboardQueryFilters(
  config: DashboardConfig,
  widget: DashboardWidget,
  globalFilters: Record<string, unknown>,
  resolveField: (member: string) => { field: string; fallbackFields?: string[] },
): DexieQueryFilter[] {
  const extra: DexieQueryFilter[] = [];

  for (const [filterKey, rawEntry] of Object.entries(globalFilters)) {
    if (rawEntry === null || rawEntry === undefined || rawEntry === '') continue;

    // Find matching filter widget or global filter definition
    const filterWidget = config.widgets.find((w) => w.type === 'filter' && w.id === filterKey);
    const globalDef = config.globalFilters?.find((g) => g.id === filterKey);

    const targetWidgets = filterWidget?.filterConfig?.targetWidgets;
    const hasExplicitTarget = Boolean(
      targetWidgets && targetWidgets.length > 0 && targetWidgets.includes(widget.id),
    );
    if (targetWidgets && targetWidgets.length > 0 && !hasExplicitTarget) {
      continue;
    }

    const isFilterValueObj = isFilterValueLike(rawEntry);
    const rawVal: unknown = isFilterValueObj ? rawEntry.value : rawEntry;
    const rawOp: string | undefined = isFilterValueObj
      ? String(rawEntry.operator ?? '') || undefined
      : undefined;

    const memberField =
      (isFilterValueObj ? rawEntry.field : undefined) ??
      filterWidget?.filterConfig?.field ??
      (filterWidget as { config?: { field?: string } } | undefined)?.config?.field ??
      globalDef?.field ??
      filterKey;

    // Cube compatibility: an unscoped global filter must only reach widgets bound
    // to a compatible cube. Prefer the filter widget's configured cubeId; when it
    // is absent, infer the member's cube prefix only if it matches a cube actually
    // used by a widget on this dashboard. Filters with no resolvable cube keep
    // the legacy apply-to-all behavior, and an explicit `targetWidgets` list
    // (checked above) still overrides scoping.
    const widgetCubeId = widget.dataSource?.cubeId;
    if (widgetCubeId && !hasExplicitTarget) {
      const memberCube = memberField.includes('.') ? memberField.split('.')[0] : undefined;
      const knownCubes = new Set(
        (config.widgets ?? [])
          .map((w) => w.dataSource?.cubeId)
          .filter((c): c is string => Boolean(c)),
      );
      const filterCubeId =
        filterWidget?.dataSource?.cubeId ??
        (memberCube && knownCubes.has(memberCube) ? memberCube : undefined);
      if (filterCubeId && filterCubeId !== widgetCubeId) continue;
    }

    const { field, fallbackFields } = resolveField(memberField);

    if (typeof rawVal === 'string' || typeof rawVal === 'number') {
      const fc = filterWidget?.filterConfig as { type?: string; filterType?: string } | undefined;
      const isTextWidget = fc?.type === 'text' || fc?.filterType === 'text';
      const mappedOp: FilterOperator =
        rawOp === 'contains' || (!rawOp && isTextWidget)
          ? 'contains'
          : rawOp === 'gt' || rawOp === 'gte' || rawOp === 'lt' || rawOp === 'lte'
          ? rawOp
          : rawOp === 'ne' || rawOp === 'neq' || rawOp === 'notEquals'
          ? 'notEquals'
          : 'equals';
      const isRelational =
        mappedOp === 'gt' || mappedOp === 'gte' || mappedOp === 'lt' || mappedOp === 'lte';
      const operand =
        typeof rawVal === 'number'
          ? rawVal
          : isRelational && String(rawVal).trim() !== '' && !Number.isNaN(Number(rawVal))
          ? Number(rawVal)
          : String(rawVal);
      extra.push({ field, operator: mappedOp, values: [operand], ...(fallbackFields ? { fallbackFields } : {}) });
    } else if (Array.isArray(rawVal)) {
      if (rawVal.length === 0) continue;
      if (rawOp === 'between' && rawVal.length === 2) {
        extra.push({
          field,
          operator: 'inDateRange',
          values: [String(rawVal[0] ?? ''), String(rawVal[1] ?? '')],
          ...(fallbackFields ? { fallbackFields } : {}),
        });
      } else {
        extra.push({ field, operator: 'equals', values: rawVal.map(String), ...(fallbackFields ? { fallbackFields } : {}) });
      }
    } else if (typeof rawVal === 'object' && rawVal !== null && ('from' in rawVal || 'to' in rawVal)) {
      const rangeVal = rawVal as { from?: string; to?: string };
      if (rangeVal.from || rangeVal.to) {
        extra.push({
          field,
          operator: 'inDateRange',
          values: [rangeVal.from ?? '', rangeVal.to ?? ''],
          ...(fallbackFields ? { fallbackFields } : {}),
        });
      }
    }
  }

  return extra;
}
